/**
 * ScopeSelector — 2단계 scope(그룹 → 계통) 선택기.
 *
 * 기본 흐름은 종전과 같다: 그룹 카드(Primary/Secondary/BOP)를 켜면 그룹 전체.
 * "세분" 을 펼치면 그 그룹의 계통 체크리스트가 나오고, 하나라도 손대면 그룹은
 * "부분 담당"(선택한 계통 목록)으로 바뀐다. 계통을 전부 켜면 다시 그룹 전체로
 * 접힌다(normalizeScopes). 값은 항상 정규화된 ScopeId[].
 *
 * `available`(프로젝트 scope)을 주면 표시와 모든 변경을 그 안으로 잘라 낸다(restrictScopes) —
 * 범위 밖 항목이 "체크됐는데 못 끄는" 상태는 생기지 않는다. 값 자체의 불변식(값 ⊆ 프로젝트
 * scope)은 값의 소유자가 자기 경계에서 지킨다(NewModelDialog 초기값/변경/제출, Step2 의
 * 파티션 축소, validateProjectData). 비어 있는 available 은 "제한 없음"(isScopeRestriction).
 */

import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioOffIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  ROLE_LABELS,
  SCOPE_CATALOG,
  isGroupSelected,
  isGroupTouched,
  isGroupWholeSelectable,
  isScopeRestriction,
  isSystemSelected,
  restrictScopes,
  roleCountsForCoverage,
  scopeGroupOf,
  scopeLabel,
  scopeShortLabel,
  selectableSystems,
  setSystemsSelected,
  toggleGroupScope,
  toggleSystemScope,
  type ModelRole,
  type ScopeGroup,
  type ScopeId,
} from '@/types/scope';
import { SCOPE_COLORS, scopeColors } from './scopeTheme';

export interface ScopeSelectorProps {
  value: ScopeId[];
  onChange: (next: ScopeId[]) => void;
  /** 고를 수 있는 범위(프로젝트 scope). 없거나 비어 있으면 전부 */
  available?: ScopeId[];
  /** 카드형(위저드 Step2) / 컴팩트형(파티션 카드·모델 다이얼로그) */
  variant?: 'cards' | 'compact';
  disabled?: boolean;
}

const ScopeSelector: React.FC<ScopeSelectorProps> = ({
  value,
  onChange,
  available,
  variant = 'cards',
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState<Set<ScopeGroup>>(() => new Set());
  const compact = variant === 'compact';
  const restriction = isScopeRestriction(available) ? available : undefined;

  // 표시·편집은 항상 정규화되고 available 안으로 잘린 값 위에서 한다(범위 밖 값은 그리지도
  // 않는다). 부모 상태를 되돌려 쓰지는 않는다 — 값의 소유자(폼/스토어)가 자기 경계에서
  // restrictScopes 로 지키고, 여기서는 사용자 조작 결과만 잘라서 낸다.
  const current = useMemo(() => restrictScopes(value, restriction), [value, restriction]);
  const emit = (next: ScopeId[]) => onChange(restrictScopes(next, restriction));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1.25 }}>
      {SCOPE_CATALOG.map((groupDef) => {
        const group = groupDef.id;
        const color = SCOPE_COLORS[group];
        const systems = selectableSystems(group, restriction);
        const wholeSelectable = isGroupWholeSelectable(group, restriction);
        const groupAvailable = wholeSelectable || systems.length > 0;
        // "전체" 판정은 고를 수 있는 범위 기준: 그룹 전체가 선택 가능하면 카탈로그 전부,
        // 프로젝트가 일부 계통만 열어 뒀으면 그 계통 전부 = 전체 (안 그러면 "일부"로 보이는데
        // 클릭하면 전부 꺼지는 모순이 생긴다)
        const whole = wholeSelectable
          ? isGroupSelected(current, group)
          : systems.length > 0 && systems.every((s) => isSystemSelected(current, s.id));
        const partial = !whole && isGroupTouched(current, group);
        const isOpen = expanded.has(group);

        const toggleWhole = () => {
          if (disabled || !groupAvailable) return;
          if (wholeSelectable) {
            emit(toggleGroupScope(current, group));
            return;
          }
          // 그룹 전체는 불가(프로젝트 scope가 일부 계통만) → 가능한 계통 전부 on/off
          const ids = systems.map((s) => s.id);
          const allOn = ids.every((id) => isSystemSelected(current, id));
          emit(setSystemsSelected(current, ids, !allOn));
        };

        const stateIcon = whole ? (
          <CheckCircleIcon sx={{ fontSize: compact ? 18 : 20, color: color.color }} aria-label="전체 선택" />
        ) : partial ? (
          <RemoveCircleOutlineIcon sx={{ fontSize: compact ? 18 : 20, color: color.color }} aria-label="일부 선택" />
        ) : (
          <RadioOffIcon sx={{ fontSize: compact ? 18 : 20, color: 'action.disabled' }} aria-label="선택 안 됨" />
        );

        return (
          <Box
            key={group}
            data-testid={`scope-group-${group}`}
            sx={{
              border: '1px solid',
              borderColor: whole || partial ? `color-mix(in oklch, ${color.color} 50%, transparent)` : 'divider',
              borderRadius: 1.5,
              bgcolor: whole || partial ? `color-mix(in oklch, ${color.color} 10%, transparent)` : 'background.paper',
              opacity: groupAvailable ? 1 : 0.5,
              overflow: 'hidden',
            }}
          >
            {/* 그룹 행 */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '4px 1fr auto',
                alignItems: 'center',
                gap: 1,
                pr: 0.5,
                py: compact ? 0.75 : 1.5,
              }}
            >
              <Box sx={{ width: 4, height: '100%', bgcolor: color.color, opacity: whole || partial ? 1 : 0.5 }} />
              <Box
                role="button"
                tabIndex={groupAvailable && !disabled ? 0 : -1}
                aria-pressed={whole}
                aria-disabled={!groupAvailable || disabled}
                data-testid={`scope-group-toggle-${group}`}
                onClick={toggleWhole}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleWhole();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 1,
                  minWidth: 0,
                  cursor: groupAvailable && !disabled ? 'pointer' : 'not-allowed',
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1, borderRadius: 1 },
                }}
              >
                {stateIcon}
                <Typography sx={{ fontSize: compact ? '0.875rem' : '1rem', fontWeight: 500, flexShrink: 0 }}>
                  {groupDef.short}
                </Typography>
                {partial && (
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                    일부 — {current.filter((id) => scopeGroupOf(id) === group).length}개 계통
                  </Typography>
                )}
                {whole && !compact && (
                  <Typography variant="caption" color="text.secondary">
                    {wholeSelectable ? '전체' : `전체 (프로젝트 범위 ${systems.length}개 계통)`}
                  </Typography>
                )}
              </Box>
              <Tooltip title={isOpen ? '계통 목록 접기' : '계통 단위로 세분 선택'}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`${groupDef.short} 계통 세분`}
                    data-testid={`scope-group-expand-${group}`}
                    disabled={!groupAvailable || systems.length === 0}
                    onClick={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(group)) next.delete(group);
                        else next.add(group);
                        return next;
                      })
                    }
                  >
                    {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* 계통 체크리스트 */}
            <Collapse in={isOpen} timeout={150} unmountOnExit>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  columnGap: 1,
                  rowGap: 0,
                  pl: 2,
                  pr: 1,
                  pb: 1,
                }}
              >
                {systems.map((sys) => {
                  const on = isSystemSelected(current, sys.id);
                  return (
                    <Box
                      key={sys.id}
                      component="label"
                      data-testid={`scope-system-${sys.id}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.5,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        minWidth: 0,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={on}
                        disabled={disabled}
                        onChange={() => emit(toggleSystemScope(current, sys.id))}
                        sx={{ p: 0.5, color: color.color, '&.Mui-checked': { color: color.color } }}
                        inputProps={{ 'aria-label': sys.label }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 0, lineHeight: 1.25, py: 0.25, overflowWrap: 'anywhere' }}
                      >
                        {sys.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        );
      })}
      {current.length === 0 && !compact && (
        <Typography variant="caption" color="warning.main">최소 1개 이상의 Scope를 선택해주세요.</Typography>
      )}
    </Box>
  );
};

export default ScopeSelector;

// ------------------------------------------------------------- 읽기 전용 칩

export interface ScopeChipProps {
  id: string;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

/** scope 칩 — 짧은 라벨(`Primary › SG`) + 그룹 색, 전체 라벨은 title */
export const ScopeChip: React.FC<ScopeChipProps> = ({ id, size = 'small', sx }) => {
  const c = scopeColors(id);
  return (
    <Chip
      label={scopeShortLabel(id)}
      title={scopeLabel(id)}
      size={size}
      variant="outlined"
      sx={[{ bgcolor: c.bg, color: c.color, borderColor: c.border, fontWeight: 500 }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
};

export const ScopeChips: React.FC<{
  scopes: readonly string[];
  size?: 'small' | 'medium';
  emptyLabel?: string;
  chipSx?: SxProps<Theme>;
}> = ({ scopes, size = 'small', emptyLabel, chipSx }) => {
  if (scopes.length === 0) {
    return emptyLabel ? (
      <Typography variant="caption" color="text.disabled">{emptyLabel}</Typography>
    ) : null;
  }
  return (
    <>
      {scopes.map((id) => (
        <ScopeChip key={id} id={id} size={size} sx={chipSx} />
      ))}
    </>
  );
};

/** 역할 칩 — system 은 표시하지 않는다(기본값이라 노이즈) */
export const RoleChip: React.FC<{ role?: ModelRole; size?: 'small' | 'medium'; sx?: SxProps<Theme> }> = ({
  role,
  size = 'small',
  sx,
}) => {
  if (!role || roleCountsForCoverage(role)) return null;
  return (
    <Chip
      label={ROLE_LABELS[role]}
      size={size}
      variant="outlined"
      data-testid={`role-chip-${role}`}
      sx={[
        { fontWeight: 500, borderStyle: 'dashed', color: 'text.secondary', borderColor: 'text.disabled' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};
